<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" :disabled="submitting" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          取消
        </button>
        <template v-if="!loading && !error">
          <button
            v-if="currentStep > 0"
            type="button"
            class="btn btn-outline-secondary"
            :disabled="submitting"
            @click="previousStep"
          >
            <i class="ri-arrow-left-line"></i>
            上一步
          </button>
          <button
            v-if="currentStep < steps.length - 1"
            type="button"
            class="btn btn-primary"
            :disabled="submitting || !canProceedToNextStep"
            @click="nextStep"
          >
            下一步
            <i class="ri-arrow-right-line"></i>
          </button>
          <button
            v-if="currentStep === steps.length - 1"
            type="button"
            class="btn btn-primary"
            :disabled="submitting || !form.templateId || (targetAudience === 'member' && selectedMembers.length === 0 && !useFilterMode)"
            @click="handleSubmit"
          >
            <span v-if="submitting">
              <i class="ri-loader-4-line spin"></i>
              發放中...
            </span>
            <span v-else-if="targetAudience === 'friend'">
              <i class="ri-mail-send-line"></i>
              確認批次發送給 LINE 好友
            </span>
            <span v-else>
              <i class="ri-mail-send-line"></i>
              確認批次發送（{{ totalQuantity }} 張）
            </span>
          </button>
        </template>
      </div>
    </div>

    <div class="page-container">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="handleBack">返回</button>
    </div>

    <div v-else class="form-container">
      <!-- 步驟導航 -->
      <div class="stepper">
        <div class="stepper-header">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="step-item"
            :class="{
              active: currentStep === index,
              completed: currentStep > index,
              disabled: currentStep < index
            }"
          >
            <div class="step-number">
              <span v-if="currentStep > index" class="step-check">
                <i class="ri-check-line"></i>
              </span>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <div class="step-label">{{ step.label }}</div>
          </div>
        </div>
        <div class="stepper-line">
          <div class="stepper-progress" :style="{ width: `${(currentStep / (steps.length - 1)) * 100}%` }"></div>
        </div>
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- 步驟一：選擇優惠券 -->
        <div v-show="currentStep === 0" class="step-content">
          <div class="form-section">
            <h2 class="section-title">步驟一：選擇優惠券模板</h2>
          
          <div class="form-group">
            <label class="form-label">優惠券模板 <span class="required">*</span></label>
            <select
              v-model="form.templateId"
              class="form-control"
              :class="{ 'is-invalid': errors.templateId }"
              required
              :disabled="templatesLoading"
              @change="handleTemplateChange"
            >
              <option value="">請選擇優惠券模板</option>
              <option v-for="template in templates" :key="template.id" :value="template.id">
                {{ template.templateName }} - {{ formatCouponTypeText(template.couponType) }} {{ formatDiscountValue(template) }}
              </option>
            </select>
            <div v-if="templatesLoading" class="form-text">載入中...</div>
            <div v-if="errors.templateId" class="invalid-feedback">{{ errors.templateId }}</div>
          </div>

          <!-- 模板資訊 -->
          <div v-if="selectedTemplate" class="template-info-card">
            <div class="card-header">
              <h3>模板資訊</h3>
            </div>
            <div class="template-details">
              <div class="detail-row">
                <span class="label">模板名稱：</span>
                <span class="value">{{ selectedTemplate.templateName }}</span>
              </div>
              <div class="detail-row">
                <span class="label">優惠券類型：</span>
                <span :class="getCouponTypeBadgeClass(selectedTemplate.couponType)">
                  {{ formatCouponTypeText(selectedTemplate.couponType) }}
                </span>
              </div>
              <div class="detail-row">
                <span class="label">折扣值：</span>
                <span class="value">{{ formatDiscountValue(selectedTemplate) }}</span>
              </div>
              <div v-if="selectedTemplate.minPurchaseAmount > 0" class="detail-row">
                <span class="label">最低消費：</span>
                <span class="value">${{ formatNumber(selectedTemplate.minPurchaseAmount) }}</span>
              </div>
              <div class="detail-row">
                <span class="label">有效期：</span>
                <span class="value">{{ formatValidityPeriod(selectedTemplate) }}</span>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- 步驟二：選擇對象 -->
        <div v-show="currentStep === 1" class="step-content">
          <div class="form-section">
            <h2 class="section-title">步驟二：選擇發放對象</h2>

            <!-- 對象類型選擇 -->
            <div class="audience-type-selector">
              <label class="form-label">發放給</label>
              <div class="audience-radio-group">
                <label class="audience-radio-item" :class="{ active: targetAudience === 'member' }">
                  <input type="radio" v-model="targetAudience" value="member" />
                  <i class="ri-user-line"></i>
                  <span class="radio-label">會員</span>
                  <span class="radio-desc">寫入會員票券資料</span>
                </label>
                <label class="audience-radio-item" :class="{ active: targetAudience === 'friend' }">
                  <input type="radio" v-model="targetAudience" value="friend" />
                  <i class="ri-chat-smile-2-line"></i>
                  <span class="radio-label">LINE 好友</span>
                  <span class="radio-desc">直接推播給好友</span>
                </label>
              </div>
            </div>

            <!-- 好友模式設定 -->
            <div v-if="targetAudience === 'friend'" class="friend-selection-section">
              <label class="form-label">好友篩選方式</label>
              <div class="friend-mode-radio-group">
                <label class="friend-mode-item" :class="{ active: friendTargetMode === 'all_friends' }">
                  <input type="radio" v-model="friendTargetMode" value="all_friends" />
                  <i class="ri-group-line"></i>
                  <span>全部好友</span>
                </label>
                <label class="friend-mode-item" :class="{ active: friendTargetMode === 'line_tags' }">
                  <input type="radio" v-model="friendTargetMode" value="line_tags" />
                  <i class="ri-price-tag-3-line"></i>
                  <span>依 LINE 標籤篩選</span>
                </label>
              </div>

              <div v-if="friendTargetMode === 'line_tags'" class="form-group" style="margin-top: 12px;">
                <TagSelectorMultiple
                  v-model="selectedLineTags"
                  label="選擇標籤"
                  hint="選擇一個或多個標籤，符合條件的 LINE 好友將收到票券推播"
                  :show-selected="true"
                />
                <div v-if="selectedLineTags.length > 1" style="margin-top: 12px;">
                  <TagMatchTypeToggle
                    v-model="lineTagMatchType"
                    label="符合方式"
                  />
                </div>
              </div>

              <div v-if="friendTargetMode === 'all_friends'" class="info-box">
                <i class="ri-information-line"></i>
                <span>將派券給所有未封鎖的 LINE 好友，並透過推播通知他們</span>
              </div>

              <!-- 好友數量預覽 -->
              <div class="friend-count-preview">
                <span v-if="friendCountLoading" class="friend-count-loading">
                  <i class="ri-loader-4-line spin-icon"></i> 計算中...
                </span>
                <span v-else-if="friendCount !== null" class="friend-count-result">
                  <i class="ri-group-line"></i>
                  符合條件的好友：<strong>{{ friendCount }} 位</strong>
                </span>
              </div>
            </div>

          <!-- 會員選擇 Tab（僅會員模式顯示） -->
          <div v-if="targetAudience === 'member'">
          <!-- Tab 切換 -->
          <div class="selection-tabs">
            <button
              type="button"
              :class="['tab-button', { active: activeTab === 'search' }]"
              @click="activeTab = 'search'"
            >
              <i class="ri-search-line"></i>
              搜尋會員
            </button>
            <button
              type="button"
              :class="['tab-button', { active: activeTab === 'tags' }]"
              @click="activeTab = 'tags'"
            >
              <i class="ri-price-tag-3-line"></i>
              標籤搜尋
            </button>
            <button
              type="button"
              :class="['tab-button', { active: activeTab === 'csv' }]"
              @click="activeTab = 'csv'"
            >
              <i class="ri-file-list-3-line"></i>
              批量輸入
            </button>
            <button
              type="button"
              :class="['tab-button', { active: activeTab === 'filter' }]"
              @click="activeTab = 'filter'"
            >
              <i class="ri-filter-3-line"></i>
              進階篩選
            </button>
          </div>

          <MemberSearch
            v-show="activeTab === 'search'"
            ref="searchRef"
            v-model="selectedMembers"
          />
          <MemberTagSearch
            v-show="activeTab === 'tags'"
            ref="tagSearchRef"
            v-model="selectedMembers"
          />
          <MemberCsvUpload
            v-show="activeTab === 'csv'"
            ref="csvRef"
            v-model="selectedMembers"
          />

          <!-- 進階篩選方式 -->
          <div v-show="activeTab === 'filter'" class="selection-content">
            <div class="filter-sections">
              <!-- 標籤篩選 -->
              <div class="filter-section-card">
                <div class="filter-section-header" @click="toggleFilterSection('tags')">
                  <h4 class="filter-section-title">
                    <i class="ri-price-tag-3-line"></i>
                    標籤篩選
                  </h4>
                  <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.tags }]"></i>
                </div>
                <div v-if="filterSectionsExpanded.tags" class="filter-section-body">
                  <div class="form-group">
                    <TagMatchTypeToggle v-model="filterConfig.tagLogic" />
                  </div>
                  <TagSelectorMultiple
                    label="選擇標籤"
                    :model-value="filterConfig.tags"
                    @update:modelValue="filterConfig.tags = $event"
                    :show-selected="true"
                  />
                  <div v-if="filterConfig.tags.length > 0" class="form-group" style="margin-top: 16px;">
                    <TagSelectorMultiple
                      label="排除標籤"
                      hint="擁有這些標籤的會員將被排除在外"
                      :model-value="filterConfig.excludeTags"
                      @update:modelValue="filterConfig.excludeTags = $event"
                      :show-selected="true"
                    />
                  </div>
                </div>
              </div>

              <!-- 會員等級篩選 -->
              <div class="filter-section-card">
                <div class="filter-section-header" @click="toggleFilterSection('tiers')">
                  <h4 class="filter-section-title">
                    <i class="ri-vip-crown-line"></i>
                    會員等級篩選
                  </h4>
                  <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.tiers }]"></i>
                </div>
                <div v-if="filterSectionsExpanded.tiers" class="filter-section-body">
                  <div class="form-group">
                    <label class="form-label">選擇會員等級</label>
                    <div v-if="tiersLoading" class="form-text">載入等級中...</div>
                    <div v-else class="tier-selector">
                      <div
                        v-for="tier in availableTiers"
                        :key="tier.id"
                        class="tier-item"
                        :class="{ selected: filterConfig.tiers.includes(tier.id) }"
                        @click="toggleFilterTier(tier.id)"
                      >
                        <span>{{ tier.tierName }}</span>
                        <div v-if="filterConfig.tiers.includes(tier.id)" class="check-icon">
                          <i class="ri-check-line"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- RFM 動態狀態篩選 -->
              <div class="filter-section-card">
                <div class="filter-section-header" @click="toggleFilterSection('rfm')">
                  <h4 class="filter-section-title">
                    <i class="ri-bar-chart-line"></i>
                    RFM 動態狀態篩選
                  </h4>
                  <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.rfm }]"></i>
                </div>
                <div v-if="filterSectionsExpanded.rfm" class="filter-section-body">
                  <div class="form-group">
                    <label class="form-label">RFM 分群</label>
                    <select
                      v-model="filterConfig.rfmSegments"
                      multiple
                      class="form-control"
                      size="6"
                    >
                      <option value="champions">冠軍客戶</option>
                      <option value="loyal_customers">忠誠客戶</option>
                      <option value="potential_loyalists">潛力忠誠客戶</option>
                      <option value="new_customers">新客戶</option>
                      <option value="promising">有潛力客戶</option>
                      <option value="needs_attention">需要關注</option>
                      <option value="about_to_sleep">即將沉睡</option>
                      <option value="cannot_lose_them">不能失去</option>
                      <option value="at_risk">有風險</option>
                      <option value="hibernating">休眠客戶</option>
                      <option value="lost">流失客戶</option>
                    </select>
                    <small class="form-text">可多選，按住 Ctrl (Windows) 或 Cmd (Mac) 進行多選</small>
                  </div>
                  <div class="form-group">
                    <label class="form-label">R 分數範圍（最近購買）</label>
                    <div class="range-inputs">
                      <input
                        v-model.number="filterConfig.rScore[0]"
                        type="number"
                        class="form-control"
                        placeholder="最小值"
                        min="1"
                        max="5"
                      />
                      <span>~</span>
                      <input
                        v-model.number="filterConfig.rScore[1]"
                        type="number"
                        class="form-control"
                        placeholder="最大值"
                        min="1"
                        max="5"
                      />
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="form-label">F 分數範圍（購買頻率）</label>
                    <div class="range-inputs">
                      <input
                        v-model.number="filterConfig.fScore[0]"
                        type="number"
                        class="form-control"
                        placeholder="最小值"
                        min="1"
                        max="5"
                      />
                      <span>~</span>
                      <input
                        v-model.number="filterConfig.fScore[1]"
                        type="number"
                        class="form-control"
                        placeholder="最大值"
                        min="1"
                        max="5"
                      />
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="form-label">M 分數範圍（購買金額）</label>
                    <div class="range-inputs">
                      <input
                        v-model.number="filterConfig.mScore[0]"
                        type="number"
                        class="form-control"
                        placeholder="最小值"
                        min="1"
                        max="5"
                      />
                      <span>~</span>
                      <input
                        v-model.number="filterConfig.mScore[1]"
                        type="number"
                        class="form-control"
                        placeholder="最大值"
                        min="1"
                        max="5"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- 消費行為篩選 -->
              <div class="filter-section-card">
                <div class="filter-section-header" @click="toggleFilterSection('behavior')">
                  <h4 class="filter-section-title">
                    <i class="ri-shopping-cart-line"></i>
                    消費行為篩選
                  </h4>
                  <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.behavior }]"></i>
                </div>
                <div v-if="filterSectionsExpanded.behavior" class="filter-section-body">
                  <div class="form-group">
                    <label class="form-label">累積消費金額</label>
                    <div class="range-inputs">
                      <input
                        v-model.number="filterConfig.minTotalSpent"
                        type="number"
                        class="form-control"
                        placeholder="最低金額"
                        min="0"
                      />
                      <span>~</span>
                      <input
                        v-model.number="filterConfig.maxTotalSpent"
                        type="number"
                        class="form-control"
                        placeholder="最高金額"
                        min="0"
                      />
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="form-label">消費次數</label>
                    <div class="range-inputs">
                      <input
                        v-model.number="filterConfig.minPurchaseCount"
                        type="number"
                        class="form-control"
                        placeholder="最低次數"
                        min="0"
                      />
                      <span>~</span>
                      <input
                        v-model.number="filterConfig.maxPurchaseCount"
                        type="number"
                        class="form-control"
                        placeholder="最高次數"
                        min="0"
                      />
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="form-label">年度消費金額</label>
                    <div class="range-inputs">
                      <input
                        v-model.number="filterConfig.minYearlySpent"
                        type="number"
                        class="form-control"
                        placeholder="最低金額"
                        min="0"
                      />
                      <span>~</span>
                      <input
                        v-model.number="filterConfig.maxYearlySpent"
                        type="number"
                        class="form-control"
                        placeholder="最高金額"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- 時間篩選 -->
              <div class="filter-section-card">
                <div class="filter-section-header" @click="toggleFilterSection('time')">
                  <h4 class="filter-section-title">
                    <i class="ri-calendar-line"></i>
                    時間篩選
                  </h4>
                  <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.time }]"></i>
                </div>
                <div v-if="filterSectionsExpanded.time" class="filter-section-body">
                  <div class="form-group">
                    <label class="form-label">註冊日期範圍</label>
                    <div class="date-range-inputs">
                      <input
                        v-model="filterConfig.registrationDateFrom"
                        type="date"
                        class="form-control"
                        placeholder="開始日期"
                      />
                      <span>~</span>
                      <input
                        v-model="filterConfig.registrationDateTo"
                        type="date"
                        class="form-control"
                        placeholder="結束日期"
                      />
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="form-label">最後消費距今天數</label>
                    <div class="range-inputs">
                      <input
                        v-model.number="filterConfig.lastPurchaseDaysMin"
                        type="number"
                        class="form-control"
                        placeholder="最少天數"
                        min="0"
                      />
                      <span>~</span>
                      <input
                        v-model.number="filterConfig.lastPurchaseDaysMax"
                        type="number"
                        class="form-control"
                        placeholder="最多天數"
                        min="0"
                      />
                    </div>
                    <small class="form-text">例如：設定 30~90 表示最後消費距今 30-90 天的會員</small>
                  </div>
                </div>
              </div>

              <!-- 排除條件 -->
              <div class="filter-section-card">
                <div class="filter-section-header" @click="toggleFilterSection('exclusion')">
                  <h4 class="filter-section-title">
                    <i class="ri-close-circle-line"></i>
                    排除條件
                  </h4>
                  <i :class="['ri-arrow-down-s-line', { 'rotated': filterSectionsExpanded.exclusion }]"></i>
                </div>
                <div v-if="filterSectionsExpanded.exclusion" class="filter-section-body">
                  <div class="form-group">
                    <label class="form-label">
                      <input
                        v-model="filterConfig.excludeAlreadyIssued"
                        type="checkbox"
                      />
                      排除已領取該優惠券的會員
                    </label>
                  </div>
                  <div class="form-group">
                    <label class="form-label">
                      <input
                        v-model="filterConfig.excludeInactive"
                        type="checkbox"
                      />
                      排除未啟用會員
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- 預覽按鈕和結果 -->
            <div class="filter-preview-section">
              <div class="form-group">
                <button
                  type="button"
                  class="btn btn-primary"
                  @click="previewFilteredMembers"
                  :disabled="previewing"
                >
                  <span v-if="previewing">
                    <i class="ri-loader-4-line spin"></i>
                    預覽中...
                  </span>
                  <span v-else>
                    <i class="ri-eye-line"></i>
                    預覽符合條件的會員
                  </span>
                </button>
              </div>

              <!-- 預覽結果 -->
              <div v-if="previewResult" class="preview-result-card">
                <div class="preview-header">
                  <h4>預覽結果</h4>
                  <span class="preview-count">符合條件：{{ previewResult.estimatedMemberCount }} 位會員</span>
                </div>
                <div v-if="previewResult.previewMembers && previewResult.previewMembers.length > 0" class="preview-members-table">
                  <table>
                    <thead>
                      <tr>
                        <th>姓名</th>
                        <th>電話</th>
                        <th>等級</th>
                        <th>累積消費</th>
                        <th>消費次數</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="member in previewResult.previewMembers" :key="member.id">
                        <td>{{ member.name }}</td>
                        <td>{{ member.phone }}</td>
                        <td>{{ member.memberTier || '-' }}</td>
                        <td>${{ formatNumber(member.totalSpent) }}</td>
                        <td>{{ member.purchaseCount }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="previewResult.estimatedMemberCount > 10" class="preview-note">
                    <small>僅顯示前 10 筆，共 {{ previewResult.estimatedMemberCount }} 位會員</small>
                  </div>
                </div>
                <div v-else class="preview-empty">
                  <i class="ri-inbox-line"></i>
                  <p>沒有符合條件的會員</p>
                </div>
                <div class="preview-actions">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="useFilteredMembers"
                    :disabled="!previewResult || previewResult.estimatedMemberCount === 0"
                  >
                    <i class="ri-check-line"></i>
                    使用進階篩選發放（{{ previewResult?.estimatedMemberCount || 0 }} 位會員）
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 已選擇的會員列表 -->
          <div v-if="selectedMembers.length > 0" class="selected-members-section">
            <div class="section-header">
              <h3>已選擇的會員（{{ selectedMembers.length }} 位）</h3>
              <button
                type="button"
                class="btn btn-sm btn-link text-danger"
                @click="clearAllMembers"
              >
                <i class="ri-delete-bin-line"></i>
                清除全部
              </button>
            </div>
            <div class="selected-members-list">
              <div
                v-for="member in selectedMembers"
                :key="member.id"
                class="selected-member-item"
              >
                <div class="member-info">
                  <div class="member-name">{{ member.name }}</div>
                  <div class="member-details">
                    <span v-if="member.phone">{{ member.phone }}</span>
                    <span v-if="member.email">{{ member.email }}</span>
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-link text-danger"
                  @click="removeMember(member.id)"
                  title="移除"
                >
                  <i class="ri-close-line"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="errors.memberIds" class="error-text">{{ errors.memberIds }}</div>
          </div><!-- end v-if member -->
          </div><!-- end form-section -->
        </div><!-- end step 2 -->

        <!-- 步驟三：確認發送 -->
        <div v-show="currentStep === 2" class="step-content">
          <!-- 發放進度追蹤（當使用進階篩選模式且正在發放時顯示） -->
          <div v-if="issuanceProgress && useFilterMode" class="issuance-progress-section">
            <div class="progress-card">
              <div class="progress-header">
                <h3 class="progress-title">
                  <i class="ri-loader-4-line" :class="{ 'spin': issuanceProgress.status === 'processing' }"></i>
                  發放進度
                </h3>
                <span :class="getProgressStatusBadgeClass(issuanceProgress.status)">
                  {{ getProgressStatusText(issuanceProgress.status) }}
                </span>
              </div>
              
              <div class="progress-body">
                <!-- 進度條 -->
                <div class="progress-bar-container">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill" 
                      :style="{ width: `${issuanceProgress.progressPercentage || 0}%` }"
                    ></div>
                  </div>
                  <div class="progress-text">
                    {{ issuanceProgress.progressPercentage || 0 }}% 
                    ({{ issuanceProgress.processedCount || 0 }} / {{ issuanceProgress.targetMemberCount || 0 }})
                  </div>
                </div>

                <!-- 統計資訊 -->
                <div class="progress-stats">
                  <div class="stat-item">
                    <span class="stat-label">目標會員數：</span>
                    <span class="stat-value">{{ issuanceProgress.targetMemberCount || 0 }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">已處理：</span>
                    <span class="stat-value">{{ issuanceProgress.processedCount || 0 }}</span>
                  </div>
                  <div class="stat-item success">
                    <span class="stat-label">成功：</span>
                    <span class="stat-value">{{ issuanceProgress.successCount || 0 }}</span>
                  </div>
                  <div class="stat-item error">
                    <span class="stat-label">失敗：</span>
                    <span class="stat-value">{{ issuanceProgress.failureCount || 0 }}</span>
                  </div>
                  <div v-if="issuanceProgress.status === 'processing' && issuanceProgress.estimatedTimeRemaining" class="stat-item">
                    <span class="stat-label">預估剩餘時間：</span>
                    <span class="stat-value">{{ formatEstimatedTime(issuanceProgress.estimatedTimeRemaining) }}</span>
                  </div>
                  <div v-if="issuanceProgress.executionTimeMs" class="stat-item">
                    <span class="stat-label">執行時間：</span>
                    <span class="stat-value">{{ formatExecutionTime(issuanceProgress.executionTimeMs) }}</span>
                  </div>
                </div>

                <!-- 完成後的詳細結果 -->
                <div v-if="issuanceProgress.status === 'completed'" class="progress-completed">
                  <div class="success-message">
                    <i class="ri-checkbox-circle-line"></i>
                    <span>發放完成！成功發放給 {{ issuanceProgress.successCount || 0 }} 位會員</span>
                  </div>
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="viewIssuanceDetail"
                    >
                      <i class="ri-eye-line"></i>
                      查看詳情
                    </button>
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="resetIssuance"
                    >
                      <i class="ri-refresh-line"></i>
                      重新發放
                    </button>
                  </div>
                </div>

                <!-- 失敗後的錯誤訊息 -->
                <div v-if="issuanceProgress.status === 'failed'" class="progress-failed">
                  <div class="error-message">
                    <i class="ri-error-warning-line"></i>
                    <span>發放失敗：{{ issuanceProgress.errorMessage || '未知錯誤' }}</span>
                  </div>
                  <div class="action-buttons">
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="viewIssuanceDetail"
                    >
                      <i class="ri-eye-line"></i>
                      查看詳情
                    </button>
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="resetIssuance"
                    >
                      <i class="ri-refresh-line"></i>
                      重新發放
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <h2 class="section-title">步驟三：確認發送</h2>

            <!-- 好友模式對象摘要 -->
            <div v-if="targetAudience === 'friend'" class="confirmation-card">
              <h3 class="card-title">
                <i class="ri-chat-smile-2-line"></i>
                發放對象
              </h3>
              <div class="issue-summary">
                <div class="summary-item">
                  <span class="label">對象類型：</span>
                  <span class="value">LINE 好友</span>
                </div>
                <div class="summary-item">
                  <span class="label">篩選方式：</span>
                  <span class="value">{{ friendTargetMode === 'all_friends' ? '全部好友' : '依 LINE 標籤篩選' }}</span>
                </div>
                <div v-if="friendTargetMode === 'line_tags'" class="summary-item summary-item--tags">
                  <span class="label">標籤：</span>
                  <span class="value">
                    <span class="tag-name-list">
                      <span
                        v-for="name in (selectedLineTagNames.length > 0 ? selectedLineTagNames : [`${selectedLineTags.length} 個標籤`])"
                        :key="name"
                        class="tag-name-chip"
                      >{{ name }}</span>
                    </span>
                    <span class="match-type-badge" :class="lineTagMatchType === 'OR' ? 'badge-or' : 'badge-and'">
                      {{ lineTagMatchType }}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- 優惠券模板資訊 -->
            <div v-if="selectedTemplate" class="confirmation-card">
              <h3 class="card-title">
                <i class="ri-coupon-line"></i>
                優惠券模板資訊
              </h3>
              <div class="template-details">
                <div class="detail-row">
                  <span class="label">模板名稱：</span>
                  <span class="value">{{ selectedTemplate.templateName }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">優惠券類型：</span>
                  <span :class="getCouponTypeBadgeClass(selectedTemplate.couponType)">
                    {{ formatCouponTypeText(selectedTemplate.couponType) }}
                  </span>
                </div>
                <div class="detail-row">
                  <span class="label">折扣值：</span>
                  <span class="value">{{ formatDiscountValue(selectedTemplate) }}</span>
                </div>
                <div v-if="selectedTemplate.minPurchaseAmount > 0" class="detail-row">
                  <span class="label">最低消費：</span>
                  <span class="value">${{ formatNumber(selectedTemplate.minPurchaseAmount) }}</span>
                </div>
                <div class="detail-row">
                  <span class="label">有效期：</span>
                  <span class="value">{{ formatValidityPeriod(selectedTemplate) }}</span>
                </div>
              </div>
            </div>

            <!-- 發放設定 -->
            <div class="confirmation-card">
              <h3 class="card-title">
                <i class="ri-settings-3-line"></i>
                發放設定
              </h3>
              <div class="form-group">
                <label class="form-label">每位會員發放數量 <span class="required">*</span></label>
                <input
                  v-model.number="form.quantity"
                  type="number"
                  class="form-control"
                  :class="{ 'is-invalid': errors.quantity }"
                  placeholder="1"
                  min="1"
                  max="10"
                  required
                />
                <small class="form-text">每位會員可發放 1-10 張優惠券</small>
                <div v-if="errors.quantity" class="invalid-feedback">{{ errors.quantity }}</div>
              </div>

              <div class="form-group">
                <label class="form-label">發放原因（選填）</label>
                <textarea
                  v-model="form.reason"
                  class="form-control"
                  rows="3"
                  placeholder="請輸入發放原因，例如：年終回饋活動、會員關懷禮等"
                  maxlength="200"
                ></textarea>
                <small class="form-text">最多 200 字</small>
              </div>

              <!-- 會員模式：多管道通知選項 -->
              <div v-if="targetAudience === 'member'" class="form-group notification-channels-section">
                <div class="notification-channels-title">
                  <i class="ri-notification-3-line"></i>
                  <span>發送通知（複選）</span>
                  <small>未勾選則不發送通知</small>
                </div>
                <NotificationChannelSelector
                  v-model:channels="notificationChannels"
                  v-model:templateIds="notificationTemplateIds"
                  :line-fixed="true"
                />
              </div>
            </div>

            <!-- 已選擇的會員列表（會員模式） -->
            <div v-if="targetAudience === 'member'" class="confirmation-card">
              <h3 class="card-title">
                <i class="ri-user-line"></i>
                發送對象（{{ selectedMembers.length }} 位會員）
              </h3>
              <div class="selected-members-list-confirm">
                <div
                  v-for="member in selectedMembers"
                  :key="member.id"
                  class="selected-member-item-confirm"
                >
                  <div class="member-info">
                    <div class="member-name">{{ member.name }}</div>
                    <div class="member-details">
                      <span v-if="member.phone">{{ member.phone }}</span>
                      <span v-if="member.email">{{ member.email }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 發放統計 -->
            <div class="confirmation-card summary-card">
              <h3 class="card-title">
                <i class="ri-bar-chart-line"></i>
                發放統計
              </h3>
              <div class="issue-summary">
                <div class="summary-item">
                  <span class="label">{{ targetAudience === 'friend' ? '發送對象：' : '選擇會員數：' }}</span>
                  <span class="value">
                    <template v-if="targetAudience === 'friend'">
                      {{ friendTargetMode === 'all_friends' ? '全部 LINE 好友' : '符合標籤的 LINE 好友' }}
                      <span v-if="friendCount !== null">（{{ friendCount }} 位）</span>
                    </template>
                    <template v-else>{{ selectedMembers.length }} 位</template>
                  </span>
                </div>
                <div class="summary-item">
                  <span class="label">每位發放數量：</span>
                  <span class="value">{{ form.quantity || 1 }} 張</span>
                </div>
                <div class="summary-item total">
                  <span class="label">總發放數量：</span>
                  <span class="value">
                    <template v-if="targetAudience === 'friend'">
                      {{ friendCount !== null ? (friendCount * (form.quantity || 1)) + ' 張' : '依好友人數決定' }}
                    </template>
                    <template v-else>{{ totalQuantity }} 張</template>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 錯誤訊息 -->
        <div v-if="submitError" class="alert alert-danger">
          {{ submitError }}
        </div>
      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { tagService } from '@/api/modules/tags.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { manualIssuanceService } from '@/api/modules/manualIssuance.js';
import { showSuccess, showError } from '@/utils/swal.js';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import TagMatchTypeToggle from '@/components/TagMatchTypeToggle.vue';
import MemberSearch from '@/components/MemberSearch.vue';
import MemberTagSearch from '@/components/MemberTagSearch.vue';
import MemberCsvUpload from '@/components/MemberCsvUpload.vue';
import NotificationChannelSelector from '@/components/NotificationChannelSelector.vue';

export default {
  name: 'BatchIssueCoupon',
  components: { TagSelectorMultiple, TagMatchTypeToggle, MemberSearch, MemberTagSearch, MemberCsvUpload, NotificationChannelSelector },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('批次發送優惠券');
    
    const loading = ref(false);
    const error = ref(null);
    const submitting = ref(false);
    const submitError = ref('');
    const templates = ref([]);
    const templatesLoading = ref(false);
    const activeTab = ref('search'); // 'search' | 'tags' | 'csv' | 'filter'
    const searchRef = ref(null);
    const tagSearchRef = ref(null);
    const csvRef = ref(null);
    
    // 步驟控制
    const currentStep = ref(0);
    const steps = [
      { label: '選擇優惠券', key: 'template' },
      { label: '選擇對象', key: 'audience' },
      { label: '確認發送', key: 'confirm' }
    ];

    // 發放對象類型
    const targetAudience = ref('member'); // 'member' | 'friend'
    const friendTargetMode = ref('all_friends'); // 'all_friends' | 'line_tags'
    const selectedLineTags = ref([]); // 已選擇的 LINE 標籤 IDs
    const lineTagMatchType = ref('OR'); // 'OR'=任一標籤符合、'AND'=必須同時符合所有標籤
    const sendLineNotification = ref(false);

    // 多管道通知
    const notificationChannels = ref([]);    // 已勾選的管道 ['line','email','sms']
    const notificationTemplateIds = ref({}); // { line: '<uuid>', email: '<uuid>', sms: '<uuid>' }

    // 同步舊欄位（向下相容）
    watch(notificationChannels, (val) => {
      sendLineNotification.value = val.includes('line');
    });

    // LINE 好友數量預覽
    const friendCount = ref(null);
    const friendCountLoading = ref(false);
    let friendCountTimer = null;
    const allTagsCache = ref([]);

    const selectedLineTagNames = computed(() => {
      if (!selectedLineTags.value || selectedLineTags.value.length === 0) return [];
      if (allTagsCache.value.length === 0) return [];
      const tagMap = {};
      allTagsCache.value.forEach(t => { tagMap[t.id] = t.tagName || t.name; });
      return selectedLineTags.value.map(id => tagMap[id]).filter(Boolean);
    });

    const getTagNamesFromIds = async (tagIds) => {
      if (!tagIds || tagIds.length === 0) return [];
      if (allTagsCache.value.length === 0) {
        try {
          const res = await tagService.getTags({ pageSize: 1000 });
          if (res && res.success && res.data) {
            allTagsCache.value = Array.isArray(res.data) ? res.data : (res.data.items || []);
          }
        } catch (e) {
          return [];
        }
      }
      const tagMap = {};
      allTagsCache.value.forEach(t => { tagMap[t.id] = t.tagName || t.name; });
      return tagIds.map(id => tagMap[id]).filter(Boolean);
    };

    const fetchFriendCount = async () => {
      if (targetAudience.value !== 'friend') return;
      if (friendTargetMode.value === 'line_tags' && selectedLineTags.value.length === 0) {
        friendCount.value = 0;
        return;
      }
      friendCountLoading.value = true;
      try {
        let lineTagNames = [];
        if (friendTargetMode.value === 'line_tags') {
          lineTagNames = await getTagNamesFromIds(selectedLineTags.value);
        }
        const res = await couponService.previewFriendCount({
          friendTargetMode: friendTargetMode.value,
          lineTagNames: lineTagNames.length > 0 ? lineTagNames : undefined,
          lineTagMatchType: lineTagMatchType.value
        });
        if (res && res.success && res.data) {
          friendCount.value = res.data.count;
        }
      } catch (e) {
        friendCount.value = null;
      } finally {
        friendCountLoading.value = false;
      }
    };

    const scheduleFriendCount = () => {
      if (friendCountTimer) clearTimeout(friendCountTimer);
      friendCountTimer = setTimeout(fetchFriendCount, 500);
    };

    watch(targetAudience, (val) => {
      if (val === 'friend') scheduleFriendCount();
      else friendCount.value = null;
    });
    watch(friendTargetMode, () => {
      if (targetAudience.value === 'friend') scheduleFriendCount();
    });
    watch(selectedLineTags, () => {
      if (targetAudience.value === 'friend' && friendTargetMode.value === 'line_tags') scheduleFriendCount();
    });
    watch(lineTagMatchType, () => {
      if (targetAudience.value === 'friend' && friendTargetMode.value === 'line_tags') scheduleFriendCount();
    });
    
    const csvFileUrl = ref('');
    
    // 進階篩選方式
    const filterConfig = reactive({
      tags: [],
      tagLogic: 'OR',
      excludeTags: [],
      tiers: [],
      rfmSegments: [],
      rScore: [1, 5],
      fScore: [1, 5],
      mScore: [1, 5],
      minTotalSpent: null,
      maxTotalSpent: null,
      minPurchaseCount: null,
      maxPurchaseCount: null,
      minYearlySpent: null,
      maxYearlySpent: null,
      registrationDateFrom: null,
      registrationDateTo: null,
      lastPurchaseDaysMin: null,
      lastPurchaseDaysMax: null,
      excludeAlreadyIssued: false,
      excludeBlacklisted: false,
      excludeInactive: false,
      status: ['active'],
      memberIds: [],
      search: null
    });
    const filterSectionsExpanded = reactive({
      tags: true,
      tiers: false,
      rfm: false,
      behavior: false,
      time: false,
      exclusion: false
    });
    const availableTiers = ref([]);
    const tiersLoading = ref(false);
    const previewResult = ref(null);
    const previewing = ref(false);
    const useFilterMode = ref(false); // 是否使用進階篩選模式發放
    
    // 發放進度追蹤
    const issuanceProgress = ref(null);
    const pollingInterval = ref(null);
    const currentIssuanceId = ref(null);
    
    // 已選擇的會員
    const selectedMembers = ref([]);

    const form = reactive({
      templateId: '',
      quantity: 1,
      reason: ''
    });

    const errors = reactive({});

    const selectedTemplate = computed(() => {
      return templates.value.find(t => t.id === form.templateId);
    });

    const totalQuantity = computed(() => {
      if (targetAudience.value === 'friend') return '依好友人數決定';
      return selectedMembers.value.length * (form.quantity || 1);
    });

    // 是否可以進入下一步
    const canProceedToNextStep = computed(() => {
      if (currentStep.value === 0) {
        return !!form.templateId;
      } else if (currentStep.value === 1) {
        if (targetAudience.value === 'member') {
          return selectedMembers.value.length > 0 || useFilterMode.value;
        }
        // 好友模式：全部好友不需要額外條件；依標籤需有選擇標籤
        if (friendTargetMode.value === 'line_tags') {
          return selectedLineTags.value.length > 0;
        }
        return true;
      }
      return true;
    });

    // 下一步
    const nextStep = () => {
      if (!canProceedToNextStep.value) {
        return;
      }
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
        // 進入確認步驟時確保好友數量和標籤 cache 已準備好
        if (currentStep.value === 2 && targetAudience.value === 'friend') {
          fetchFriendCount();
        }
      }
    };

    // 上一步
    const previousStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    };

    // 標籤分類
    // 載入會員等級列表
    const loadTiers = async () => {
      tiersLoading.value = true;
      try {
        const response = await memberTierService.getTiers({
          isActive: true,
          sortBy: 'displayOrder',
          order: 'asc'
        });
        if (response && response.success && response.data) {
          availableTiers.value = Array.isArray(response.data) ? response.data : (response.data.items || []);
        }
      } catch (err) {
        console.error('載入會員等級失敗:', err);
        showError('載入會員等級失敗', err.message);
      } finally {
        tiersLoading.value = false;
      }
    };

    // 切換篩選區塊展開/收合
    const toggleFilterSection = (section) => {
      filterSectionsExpanded[section] = !filterSectionsExpanded[section];
    };

    // 切換篩選標籤
    const toggleFilterTag = (tagId) => {
      const index = filterConfig.tags.indexOf(tagId);
      if (index > -1) {
        filterConfig.tags.splice(index, 1);
      } else {
        filterConfig.tags.push(tagId);
      }
    };

    // 切換排除標籤
    const toggleExcludeTag = (tagId) => {
      const index = filterConfig.excludeTags.indexOf(tagId);
      if (index > -1) {
        filterConfig.excludeTags.splice(index, 1);
      } else {
        filterConfig.excludeTags.push(tagId);
      }
    };

    // 切換篩選等級
    const toggleFilterTier = (tierId) => {
      const index = filterConfig.tiers.indexOf(tierId);
      if (index > -1) {
        filterConfig.tiers.splice(index, 1);
      } else {
        filterConfig.tiers.push(tierId);
      }
    };

    // 預覽符合進階篩選的會員
    const previewFilteredMembers = async () => {
      if (!form.templateId) {
        showError('請先選擇優惠券模板');
        return;
      }

      previewing.value = true;
      try {
        // 準備 filterConfig
        const previewFilterConfig = {
          ...filterConfig,
          excludeAlreadyIssued: filterConfig.excludeAlreadyIssued,
          couponTemplateId: filterConfig.excludeAlreadyIssued ? form.templateId : null
        };

        // 清理空值
        Object.keys(previewFilterConfig).forEach(key => {
          if (previewFilterConfig[key] === null || 
              (Array.isArray(previewFilterConfig[key]) && previewFilterConfig[key].length === 0) ||
              previewFilterConfig[key] === '') {
            delete previewFilterConfig[key];
          }
        });

        const response = await manualIssuanceService.previewCouponIssuance({
          couponTemplateId: form.templateId,
          filterConfig: previewFilterConfig
        });

        if (response && response.success && response.data) {
          previewResult.value = response.data;
        } else {
          showError('預覽失敗', response?.message || '未知錯誤');
        }
      } catch (err) {
        console.error('預覽失敗:', err);
        showError('預覽失敗', err.message || '請檢查進階篩選');
      } finally {
        previewing.value = false;
      }
    };

    // 使用進階篩選發放
    const useFilteredMembers = () => {
      if (!previewResult.value || previewResult.value.estimatedMemberCount === 0) {
        showError('沒有符合條件的會員');
        return;
      }
      useFilterMode.value = true;
      // 進入下一步確認發送
      if (currentStep.value < steps.length - 1) {
        currentStep.value++;
      }
    };

    const fetchTemplates = async () => {
      templatesLoading.value = true;
      try {
        const response = await couponService.getTemplates({ pageSize: 100, isActive: true });
        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
          
          // 檢查 URL 參數中是否有 templateId，如果有則自動選取
          const templateId = route.query.templateId;
          if (templateId) {
            const template = templates.value.find(t => t.id === templateId);
            if (template) {
              form.templateId = templateId;
              // 如果模板已選取，自動進入下一步
              if (currentStep.value === 0) {
                nextStep();
              }
            }
          }
        }
      } catch (err) {
        console.error('獲取優惠券模板失敗:', err);
        showError('獲取優惠券模板失敗');
      } finally {
        templatesLoading.value = false;
      }
    };

    const isMemberSelected = (memberId) => {
      return selectedMembers.value.some(m => m.id === memberId);
    };

    const toggleMember = (member) => {
      const index = selectedMembers.value.findIndex(m => m.id === member.id);
      if (index > -1) {
        selectedMembers.value.splice(index, 1);
      } else {
        selectedMembers.value.push(member);
      }
    };


    const uploadCsvToS3 = async (file) => {
      csvRef.value?.setCsvUploadStatus('uploading');
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await couponService.uploadFile(formData);
        if (response && response.success && response.data?.fileUrl) {
          csvFileUrl.value = response.data.fileUrl;
          csvRef.value?.setCsvUploadStatus('done');
          return response.data.fileUrl;
        } else {
          csvRef.value?.setCsvUploadStatus('error');
          return null;
        }
      } catch (err) {
        console.error('CSV 備份上傳失敗:', err);
        csvRef.value?.setCsvUploadStatus('error');
        return null;
      }
    };

    const removeMember = (memberId) => {
      const index = selectedMembers.value.findIndex(m => m.id === memberId);
      if (index > -1) {
        selectedMembers.value.splice(index, 1);
      }
    };

    const clearAllMembers = () => {
      selectedMembers.value = [];
    };

    const handleTemplateChange = () => {
      errors.templateId = '';
    };

    const validateForm = () => {
      errors.memberIds = '';
      errors.templateId = '';
      errors.quantity = '';

      if (targetAudience.value === 'member') {
        if (selectedMembers.value.length === 0 && !useFilterMode.value) {
          errors.memberIds = '請至少選擇一位會員';
          return false;
        }
      } else if (targetAudience.value === 'friend') {
        if (friendTargetMode.value === 'line_tags' && selectedLineTags.value.length === 0) {
          showError('請至少選擇一個標籤');
          return false;
        }
      }

      if (!form.templateId) {
        errors.templateId = '請選擇優惠券模板';
        return false;
      }

      if (!form.quantity || form.quantity < 1 || form.quantity > 10) {
        errors.quantity = '發放數量必須在 1-10 之間';
        return false;
      }

      return true;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;
      submitError.value = '';

      try {
        let response;

        if (targetAudience.value === 'friend') {
          // 好友模式：將 tag IDs 轉換成 tag names（後端使用 line_user_tags.tag_name 查詢）
          let lineTagNames = [];
          if (friendTargetMode.value === 'line_tags' && selectedLineTags.value.length > 0) {
            const tagsResponse = await tagService.getTags({ pageSize: 1000 });
            if (tagsResponse && tagsResponse.success && tagsResponse.data) {
              const allTags = Array.isArray(tagsResponse.data) ? tagsResponse.data : (tagsResponse.data.items || []);
              const tagMap = {};
              allTags.forEach(tag => { tagMap[tag.id] = tag.tagName || tag.name; });
              lineTagNames = selectedLineTags.value.map(id => tagMap[id]).filter(Boolean);
            }
          }

          response = await couponService.batchIssueCoupons({
            templateId: form.templateId,
            targetAudience: 'friend',
            lineTagNames: lineTagNames.length > 0 ? lineTagNames : undefined,
            lineTagMatchType: lineTagMatchType.value,
            quantity: form.quantity,
            reason: form.reason || undefined
          });
        } else if (useFilterMode.value) {
          // 會員模式 - 進階篩選
          const filterConfigForSubmit = { ...filterConfig, excludeAlreadyIssued: filterConfig.excludeAlreadyIssued };
          Object.keys(filterConfigForSubmit).forEach(key => {
            if (filterConfigForSubmit[key] === null ||
                (Array.isArray(filterConfigForSubmit[key]) && filterConfigForSubmit[key].length === 0) ||
                filterConfigForSubmit[key] === '') {
              delete filterConfigForSubmit[key];
            }
          });

          response = await manualIssuanceService.executeCouponIssuance({
            campaignName: form.reason || '批次發放優惠券',
            couponTemplateId: form.templateId,
            filterConfig: filterConfigForSubmit,
            sendNotification: false,
            scheduleType: 'immediate'
          });
        } else {
          // 會員模式 - 手動選擇
          const memberIds = selectedMembers.value.map(m => m.id);
          const memberTargetData = {};
          // 若有 CSV 檔案，送出時才上傳備份（避免重選時產生垃圾檔案）
          const csvFile = csvRef.value?.csvFile;
          if (csvFile) {
            const fileUrl = await uploadCsvToS3(csvFile);
            if (fileUrl) {
              memberTargetData.csvFileUrl = fileUrl;
              memberTargetData.csvFileName = csvFile.name || '';
            }
          }
          response = await couponService.batchIssueCoupons({
            templateId: form.templateId,
            targetAudience: 'member',
            memberIds,
            sendLineNotification: notificationChannels.value.includes('line'),
            notificationChannels: notificationChannels.value,
            notificationTemplateIds: Object.keys(notificationTemplateIds.value).length > 0 ? notificationTemplateIds.value : undefined,
            quantity: form.quantity,
            reason: form.reason || undefined,
            targetData: Object.keys(memberTargetData).length > 0 ? memberTargetData : undefined
          });
        }

        if (response && response.success) {
          if (targetAudience.value === 'friend') {
            router.push('/client/coupons/issues/batch');
            showSuccess('批次派券任務已建立', '正在發送給 LINE 好友，可至批次紀錄查看進度');
          } else if (useFilterMode.value) {
            const issuanceId = response.data?.issuanceId || response.data?.id;
            if (issuanceId) {
              currentIssuanceId.value = issuanceId;
              issuanceProgress.value = {
                issuanceId,
                status: 'processing',
                targetMemberCount: previewResult.value?.estimatedMemberCount || 0,
                processedCount: 0,
                successCount: 0,
                failureCount: 0,
                progressPercentage: 0
              };
              startPollingProgress(issuanceId);
            } else {
              router.push('/client/coupons/issues/batch');
              showSuccess('發放任務已建立', `將發放給 ${previewResult.value?.estimatedMemberCount || 0} 位符合條件的會員`);
            }
          } else {
            router.push('/client/coupons/issues/batch');
            showSuccess(`成功批次發送優惠券給 ${selectedMembers.value.length} 位會員`);
          }
        } else {
          submitError.value = response?.message || '批次發送失敗';
        }
      } catch (err) {
        console.error('批次發送優惠券失敗:', err);
        submitError.value = err.message || '批次發送失敗';
        showError(submitError.value);
      } finally {
        submitting.value = false;
      }
    };

    const handleBack = () => {
      router.push('/client/coupons/issues/batch');
    };

    const formatCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type;
    };

    const formatDiscountValue = (template) => {
      if (template.couponType === 'discount') {
        return `${template.discountValue}%`;
      } else if (template.couponType === 'fixed_amount') {
        return `$${Number(template.discountValue).toLocaleString('zh-TW')}`;
      } else if (template.couponType === 'free_shipping') {
        return '免運費';
      } else if (template.couponType === 'gift') {
        return '贈品';
      }
      return '-';
    };

    const formatValidityPeriod = (template) => {
      if (template.validityDays) {
        return `發放後 ${template.validityDays} 天有效`;
      } else if (template.fixedStartDate && template.fixedEndDate) {
        return `${template.fixedStartDate} ~ ${template.fixedEndDate}`;
      }
      return '-';
    };

    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return Number(num).toLocaleString('zh-TW');
    };

    // 發放進度追蹤相關方法
    const startPollingProgress = (issuanceId) => {
      // 立即查詢一次
      checkIssuanceProgress(issuanceId);
      
      // 每 2 秒輪詢一次
      pollingInterval.value = setInterval(() => {
        checkIssuanceProgress(issuanceId);
      }, 2000);
    };

    const checkIssuanceProgress = async (issuanceId) => {
      try {
        const response = await manualIssuanceService.getCouponIssuanceProgress(issuanceId);
        
        if (response && response.success && response.data) {
          const data = response.data;
          const targetCount = data.targetMemberCount || 0;
          const processedCount = (data.successCount || 0) + (data.failureCount || 0);
          const progressPercentage = targetCount > 0 
            ? Math.round((processedCount / targetCount) * 100) 
            : 0;
          
          // 計算預估剩餘時間
          let estimatedTimeRemaining = null;
          if (data.status === 'processing' && data.executedAt && processedCount > 0) {
            const elapsed = Date.now() - new Date(data.executedAt).getTime();
            const avgTimePerMember = elapsed / processedCount;
            const remaining = targetCount - processedCount;
            estimatedTimeRemaining = Math.ceil((remaining * avgTimePerMember) / 1000); // 轉換為秒
          }

          issuanceProgress.value = {
            issuanceId: data.issuanceId || data.id,
            status: data.status,
            targetMemberCount: targetCount,
            processedCount,
            successCount: data.successCount || 0,
            failureCount: data.failureCount || 0,
            progressPercentage,
            estimatedTimeRemaining,
            executionTimeMs: data.executionTimeMs,
            errorMessage: data.errorMessage || null
          };

          // 如果已完成或失敗，停止輪詢
          if (data.status === 'completed' || data.status === 'failed') {
            stopPolling();
            
            if (data.status === 'completed') {
              showSuccess('發放完成', `成功發放給 ${data.successCount || 0} 位會員`);
            } else {
              showError('發放失敗', data.errorMessage || '發放過程中發生錯誤');
            }
          }
        }
      } catch (error) {
        console.error('查詢發放進度失敗:', error);
        // 不顯示錯誤，繼續輪詢
      }
    };

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value);
        pollingInterval.value = null;
      }
    };

    const resetIssuance = () => {
      stopPolling();
      issuanceProgress.value = null;
      currentIssuanceId.value = null;
      useFilterMode.value = false;
      // 回到步驟一
      currentStep.value = 0;
    };

    const viewIssuanceDetail = () => {
      if (currentIssuanceId.value) {
        router.push(`/client/coupons/issues/batch/${currentIssuanceId.value}`);
      }
    };

    const getProgressStatusBadgeClass = (status) => {
      const classMap = {
        pending: 'badge badge-secondary',
        processing: 'badge badge-info',
        completed: 'badge badge-success',
        failed: 'badge badge-danger',
        cancelled: 'badge badge-warning'
      };
      return classMap[status] || 'badge badge-secondary';
    };

    const getProgressStatusText = (status) => {
      const textMap = {
        pending: '等待中',
        processing: '處理中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      return textMap[status] || status;
    };

    const formatEstimatedTime = (seconds) => {
      if (!seconds || seconds <= 0) return '計算中...';
      if (seconds < 60) return `${seconds} 秒`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes < 60) {
        return remainingSeconds > 0 ? `${minutes} 分 ${remainingSeconds} 秒` : `${minutes} 分`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours} 小時 ${remainingMinutes} 分` : `${hours} 小時`;
    };

    const formatExecutionTime = (ms) => {
      if (!ms) return '-';
      const seconds = Math.floor(ms / 1000);
      if (seconds < 60) return `${seconds} 秒`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes < 60) {
        return remainingSeconds > 0 ? `${minutes} 分 ${remainingSeconds} 秒` : `${minutes} 分`;
      }
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours} 小時 ${remainingMinutes} 分` : `${hours} 小時`;
    };

    const getCouponTypeBadgeClass = (type) => {
      const classMap = {
        discount: 'badge badge-primary',
        fixed_amount: 'badge badge-success',
        free_shipping: 'badge badge-info',
        gift: 'badge badge-warning'
      };
      return classMap[type] || 'badge badge-secondary';
    };

    onMounted(() => {
      fetchTemplates();
      loadTiers();
    });

    // 組件卸載時清理輪詢
    onBeforeUnmount(() => {
      stopPolling();
      if (friendCountTimer) clearTimeout(friendCountTimer);
    });

    return {
      pageTitle,
      loading,
      error,
      submitting,
      submitError,
      templates,
      templatesLoading,
      selectedMembers,
      form,
      errors,
      selectedTemplate,
      totalQuantity,
      activeTab,
      searchRef,
      tagSearchRef,
      csvRef,
      // 步驟控制
      currentStep,
      steps,
      canProceedToNextStep,
      nextStep,
      previousStep,
      isMemberSelected,
      toggleMember,
      removeMember,
      clearAllMembers,
      handleTemplateChange,
      handleSubmit,
      handleBack,
      formatCouponTypeText,
      formatDiscountValue,
      formatValidityPeriod,
      formatNumber,
      getCouponTypeBadgeClass,
      // 進階篩選
      filterConfig,
      filterSectionsExpanded,
      availableTiers,
      tiersLoading,
      previewResult,
      previewing,
      useFilterMode,
      toggleFilterSection,
      toggleFilterTag,
      toggleExcludeTag,
      toggleFilterTier,
      previewFilteredMembers,
      useFilteredMembers,
      loadTiers,
      // 發放進度追蹤
      issuanceProgress,
      getProgressStatusBadgeClass,
      getProgressStatusText,
      formatEstimatedTime,
      formatExecutionTime,
      viewIssuanceDetail,
      resetIssuance,
      // 發放對象
      targetAudience,
      friendTargetMode,
      selectedLineTags,
      lineTagMatchType,
      sendLineNotification,
      // 多管道通知
      notificationChannels,
      notificationTemplateIds,
      // 好友數量預覽
      friendCount,
      friendCountLoading,
      selectedLineTagNames
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 步驟導航 */
.stepper {
  margin-bottom: 32px;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stepper-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  position: relative;
}

.step-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
}

.step-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 20px;
  left: 60%;
  right: -40%;
  height: 2px;
  background: #e9ecef;
  z-index: 0;
}

.step-item.completed:not(:last-child)::after {
  background: #28a745;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
}

.step-item.active .step-number {
  background: #007bff;
  color: #fff;
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.2);
}

.step-item.completed .step-number {
  background: #28a745;
  color: #fff;
}

.step-check {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.step-label {
  font-size: 14px;
  color: #6c757d;
  text-align: center;
  font-weight: 500;
}

.step-item.active .step-label {
  color: #007bff;
  font-weight: 600;
}

.step-item.completed .step-label {
  color: #28a745;
}

.step-item.disabled .step-label {
  color: #adb5bd;
}

.stepper-line {
  height: 2px;
  background: #e9ecef;
  border-radius: 1px;
  position: relative;
  overflow: hidden;
}

.stepper-progress {
  height: 100%;
  background: #28a745;
  transition: width 0.3s ease;
}

.step-content {
  min-height: 400px;
}

/* 確認卡片 */
.confirmation-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.confirmation-card .card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f8f9fa;
}

.confirmation-card .card-title i {
  font-size: 20px;
  color: #007bff;
}

.summary-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
}

.summary-card .card-title {
  color: #fff;
  border-bottom-color: rgba(255, 255, 255, 0.2);
}

.summary-card .card-title i {
  color: #fff;
}

.summary-card .issue-summary {
  color: #fff;
  background: transparent;
  border: none;
}

.summary-card .summary-item {
  color: #fff;
}

.summary-card .summary-item .label {
  color: rgba(255, 255, 255, 0.9);
}

.summary-card .summary-item .value {
  color: #fff;
  font-weight: 600;
}

.summary-card .summary-item.total {
  border-top-color: rgba(255, 255, 255, 0.3);
  margin-top: 16px;
  padding-top: 16px;
}

.summary-card .summary-item.total .label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  font-weight: 600;
}

.summary-card .summary-item.total .value {
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
}

.selected-members-list-confirm {
  max-height: 400px;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
}

.selected-member-item-confirm {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
}

.selected-member-item-confirm:hover {
  background: #e9ecef;
  border-color: #dee2e6;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e9ecef;
}

.form-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.required {
  color: #dc3545;
}

.member-search {
  position: relative;
}

.member-search i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.member-results {
  margin-top: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  border-bottom: 1px solid #e9ecef;
}

.member-list {
  max-height: 300px;
  overflow-y: auto;
}

.member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.member-item:hover {
  background: #f8f9fa;
}

.member-item.selected {
  background: #e7f3ff;
  border-color: #b3d9ff;
}

.member-item:last-child {
  border-bottom: none;
}

.member-info {
  flex: 1;
}

.member-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.member-details {
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 12px;
}

.check-icon {
  color: #28a745;
  font-size: 20px;
}

.no-results {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-results i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

/* CSV 上傳區塊 */
.csv-upload-section {
  padding: 4px 0;
}

.csv-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.csv-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 4px 0 0;
}

.csv-dropzone {
  border: 2px dashed #d0d7de;
  border-radius: 8px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: #fafbfc;
  position: relative;
}

.csv-dropzone:hover,
.csv-dropzone--active {
  border-color: #0d6efd;
  background: #f0f5ff;
}

.csv-dropzone--done {
  border-color: #198754;
  background: #f0fff4;
  cursor: default;
}

.csv-dropzone-icon {
  font-size: 40px;
  color: #6c757d;
  display: block;
  margin-bottom: 8px;
}

.csv-dropzone-icon--done {
  color: #198754;
}

.csv-dropzone-text {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px;
}

.csv-dropzone-hint {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.csv-reselect {
  margin-top: 8px;
  font-size: 13px;
}

.csv-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 6px 0;
}

.csv-status--uploading { color: #0d6efd; }
.csv-status--done { color: #198754; }
.csv-status--error { color: #dc3545; }

.results-overflow-hint {
  color: #6c757d;
  font-size: 13px;
}

.results-overflow-note {
  padding: 8px 12px;
  font-size: 13px;
  color: #6c757d;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 6px;
}

.selected-members-section {
  margin-top: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.selected-members-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.selected-member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.template-info-card {
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-header h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.template-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  flex-wrap: wrap;
}

.detail-row .label {
  color: #666;
  font-weight: 500;
  min-width: 100px;
}

.detail-row .value {
  color: #333;
}

.issue-summary {
  margin-top: 16px;
  padding: 16px;
  background: #e7f3ff;
  border-radius: 8px;
  border: 1px solid #b3d9ff;
}

.summary-item {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  font-size: 14px;
}

.summary-item.total {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 2px solid #b3d9ff;
  font-weight: 600;
  font-size: 16px;
}

.summary-item .label {
  color: #666;
}

.summary-item .value {
  color: #333;
  font-weight: 500;
}

.summary-item.total .value {
  color: #0066cc;
  font-size: 18px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error-text {
  color: #dc3545;
  font-size: 14px;
  margin-top: 4px;
}

.alert {
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.text-danger {
  color: #dc3545;
}

.selection-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
}

.tab-button {
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-button:hover {
  color: #333;
  background: #f8f9fa;
}

.tab-button.active {
  color: #0066cc;
  border-bottom-color: #0066cc;
  background: #f0f7ff;
}

.tab-button i {
  font-size: 16px;
}

.selection-content {
  margin-top: 16px;
}


.tag-category {
  margin-bottom: 24px;
}

.tag-category:last-child {
  margin-bottom: 0;
}

.category-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e9ecef;
}

.category-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.category-title i {
  font-size: 18px;
  color: #666;
}

.category-count {
  font-size: 14px;
  color: #999;
  font-weight: normal;
}

.tag-search-box {
  position: relative;
  margin-bottom: 16px;
}

.tag-search-box input {
  padding-right: 40px;
}

.tag-search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

.tag-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  max-height: 300px;
  overflow-y: auto;
}

.tag-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;
}

.tag-item:hover {
  border-color: #0066cc;
  background: #f0f7ff;
}

.tag-item.selected {
  border-color: #0066cc;
  background: #e7f3ff;
  color: #0066cc;
}

.tag-item i {
  font-size: 16px;
}

.tag-item .check-icon {
  margin-left: 4px;
  color: #28a745;
  font-size: 16px;
}
/* 進階篩選區塊 */
.filter-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.filter-section-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.filter-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  cursor: pointer;
  transition: background 0.2s;
}

.filter-section-header:hover {
  background: #e9ecef;
}

.filter-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.filter-section-header i.ri-arrow-down-s-line {
  transition: transform 0.3s;
}

.filter-section-header i.ri-arrow-down-s-line.rotated {
  transform: rotate(180deg);
}

.filter-section-body {
  padding: 20px;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 12px;
}

.range-inputs input {
  flex: 1;
}

.date-range-inputs {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-range-inputs input {
  flex: 1;
}

.tier-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tier-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.tier-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.tier-item.selected {
  background: #e3f2fd;
  border-color: #1976d2;
  color: #1976d2;
}

/* 預覽結果 */
.filter-preview-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.preview-result-card {
  margin-top: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.preview-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.preview-count {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.preview-members-table {
  margin-bottom: 16px;
}

.preview-members-table table {
  width: 100%;
  border-collapse: collapse;
}

.preview-members-table th,
.preview-members-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
}

.preview-members-table th {
  background: #fff;
  font-weight: 600;
  color: #333;
}

.preview-members-table tbody tr:hover {
  background: #f8f9fa;
}

.preview-note {
  margin-top: 12px;
  text-align: center;
  color: #666;
}

.preview-empty {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.preview-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.preview-actions {
  margin-top: 16px;
  text-align: center;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

/* 發放進度追蹤 */
.issuance-progress-section {
  margin-bottom: 24px;
}

.progress-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.progress-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.progress-title i.spin {
  animation: spin 1s linear infinite;
}

.progress-bar-container {
  margin-bottom: 20px;
}

.progress-bar {
  width: 100%;
  height: 24px;
  background: #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transition: width 0.3s ease;
  border-radius: 12px;
}

.progress-text {
  margin-top: 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

.progress-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #999;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.stat-item.success .stat-value {
  color: #28a745;
}

.stat-item.error .stat-value {
  color: #dc3545;
}

.progress-completed,
.progress-failed {
  margin-top: 20px;
  padding: 16px;
  border-radius: 8px;
}

.progress-completed {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.progress-failed {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
}

.success-message,
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
}

.success-message {
  color: #155724;
}

.error-message {
  color: #721c24;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 發放對象選擇 */
.audience-type-selector {
  margin-bottom: 24px;
}

.audience-radio-group {
  display: flex;
  gap: 16px;
  margin-top: 8px;
}

.audience-radio-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 16px;
  border: 2px solid #e9ecef;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.audience-radio-item input[type="radio"] {
  display: none;
}

.audience-radio-item i {
  font-size: 28px;
  color: #adb5bd;
  transition: color 0.2s;
}

.audience-radio-item .radio-label {
  font-weight: 600;
  font-size: 15px;
  color: #495057;
}

.audience-radio-item .radio-desc {
  font-size: 12px;
  color: #adb5bd;
}

.audience-radio-item.active {
  border-color: #00B900;
  background: #f0fff0;
}

.audience-radio-item.active i {
  color: #00B900;
}

/* 好友模式 */
.friend-selection-section {
  margin-top: 8px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 20px;
}

.friend-mode-radio-group {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.friend-mode-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.friend-mode-item input[type="radio"] {
  display: none;
}

.friend-mode-item.active {
  border-color: #00B900;
  background: #f0fff0;
  color: #00B900;
}

.info-box {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  background: #e8f4fd;
  border-radius: 6px;
  font-size: 13px;
  color: #0c63e4;
}

/* 好友數量預覽 */
.friend-count-preview {
  margin-top: 12px;
  min-height: 28px;
}

.friend-count-loading,
.friend-count-result {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.friend-count-loading {
  color: #6c757d;
  background: #f8f9fa;
}

.friend-count-result {
  color: #0d6efd;
  background: #e7f1ff;
}

.friend-count-result strong {
  font-size: 15px;
}

.spin-icon {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 摘要頁標籤名稱列表 */
.summary-item--tags .value {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-name-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tag-name-chip {
  display: inline-block;
  padding: 2px 8px;
  background: #e8f4fd;
  color: #0c63e4;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.match-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}

.badge-or {
  background: #fff3e0;
  color: #e65100;
}

.badge-and {
  background: #e8f5e9;
  color: #2e7d32;
}

/* OR/AND 符合方式選擇 */
.match-type-selector {
  margin-top: 4px;
}

.match-type-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.match-type-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  transition: all 0.2s;
}

.match-type-item input[type="radio"] {
  display: none;
}

.match-type-item.active {
  border-color: #667eea;
  background: #f0f2ff;
  color: #333;
}

.match-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 22px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.match-badge.or {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.match-badge.and {
  background: #fff7e6;
  color: #fa8c16;
  border: 1px solid #ffd591;
}

/* 多管道通知區塊 */
.notification-channels-section {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.notification-channels-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
}

.notification-channels-title i {
  font-size: 18px;
  color: #3b82f6;
}

.notification-channels-title small {
  font-weight: 400;
  font-size: 12px;
  color: #888;
}

.notification-channels {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notification-channel-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.channel-check-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  min-width: 90px;
  font-size: 14px;
  font-weight: 500;
  user-select: none;
}

.channel-check-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.channel-icon {
  font-size: 18px;
}

.channel-icon.line  { color: #00B900; }
.channel-icon.email { color: #3b82f6; }
.channel-icon.sms   { color: #f59e0b; }

.template-select {
  flex: 1;
  min-width: 200px;
  max-width: 360px;
  font-size: 13px;
  height: 36px;
  padding: 4px 10px;
}

.channel-fixed-hint {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}

.no-template-hint {
  font-size: 12px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
}

.no-template-hint a {
  color: #3b82f6;
  text-decoration: underline;
}

.manage-templates-link {
  margin-top: 12px;
  font-size: 13px;
}

.manage-templates-link a {
  color: #3b82f6;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.manage-templates-link a:hover {
  text-decoration: underline;
}

</style>
